'use client';

import type { ComponentPropsWithoutRef } from 'react';
import React, { useState } from 'react';
import classNames from 'classnames';
import { Locale } from '@fixter/i18n';
import { getPostcode } from '$api/postcode';
import PostcodeAutocompleteV2, { PostcodeAutocompleteV2Variations } from '../PostcodeAutocompleteV2';
import { GoogleMap } from './components/Map/Map';

import styles from './index.module.scss';

type GarageData = {
  isFixterGarage: boolean;
  garageId: string;
  lat: number;
  lng: number;
};

type LocationData = {
  name: string;
  centerLatLng: {
    lat: number;
    lng: number;
  };
  zoom: number;
};

export interface GarageMapProps extends ComponentPropsWithoutRef<'div'> {
  readonly location?: LocationData;
  readonly garages?: GarageData[];
  readonly setLatLong: ({ lat, lng }: { lat: number; lng: number }) => void;
  readonly selectedGarageId: string;
  readonly setSelectedGarageId: (garageId: string) => void;
  readonly translations: {
    readonly postCodeText: string;
    readonly hideMapText: string;
    readonly expandMapText: string;
  };
  readonly locale: Locale;
}

export function GarageMap({
  className,
  children,
  location,
  garages,
  selectedGarageId,
  setSelectedGarageId,
  translations: { postCodeText, hideMapText, expandMapText },
  setLatLong,
  locale,
  ...props
}: GarageMapProps): JSX.Element {
  const [currentPosition, setCurrentPosition] = useState({});

  const setNewPosition = (lat: number, lng: number) => {
    setCurrentPosition({ lat, lng });
    setLatLong({ lat, lng });
  };

  const setPostcode = async (postcode: string) => {
    const {
      data: {
        result: { latitude, longitude },
      },
    } = await getPostcode(postcode);

    setNewPosition(latitude, longitude);
  };

  return (
    <div className={classNames(GarageMap.displayName, styles.component, className)} {...props}>
      <div className={styles.MapContainer}>
        {locale === Locale.EN_GB && (
          <div className={styles.PostcodeAutocompleteContainer}>
            <PostcodeAutocompleteV2
              useAutocomplete
              variation={PostcodeAutocompleteV2Variations.withButton}
              onCtaButtonClick={setPostcode}
              translations={{
                textPostcode: postCodeText,
              }}
            />
          </div>
        )}

        <GoogleMap
          className="GoogleMap"
          location={location}
          currentPosition={currentPosition}
          garages={garages}
          selectedGarageId={selectedGarageId}
          setSelectedGarageId={setSelectedGarageId}
          translations={{ hideMapText, expandMapText }}
        />
      </div>
    </div>
  );
}
GarageMap.displayName = 'GarageMap';
